<template>
  <div class="kpi-card" @click="handleClick">
    <!-- Action Buttons -->
    <!-- <div class="card-actions">
      <b-button size="sm"
                :disabled="true"
                class="card-action-btn"
                :title="$t('Webportal.Edit')"
                @click.stop="$emit('edit-kpi', kpi)">
        <MoreVertical :size="12" />
      </b-button>
      <b-button size="sm"
                :disabled="true"
                class="card-action-btn card-action-btn-danger"
                :title="$t('Webportal.Delete')"
                @click.stop="$emit('delete-kpi', kpi)">
        <Trash2 :size="12" />
      </b-button>
    </div> -->

    <!-- Badges -->
    <div class="card-badges">
      <!-- <span class="card-badge">{{ "KPI.country(kpi)" }}</span>
      <span class="badge-separator">•</span>
      <span class="card-badge">{{ "KPI.wave(kpi)" }}</span>
      <span class="badge-separator">•</span> -->
      <span class="card-badge">{{ control.label }}</span>
    </div>

    <!-- Header -->
    <div class="card-header">
      <div class="card-header-left">
        <p class="card-category no-break">{{ kpiGroup.KpiName }}</p>
        <p class="card-title no-break">{{ kpi.KpiNameShort }}</p>
      </div>
      <div class="card-trend">
        <!-- <Minus :size="14" /> -->
        <span>{{ formatTrend() }}</span>
      </div>
    </div>

    <!-- Value & Target -->
    <div class="card-value">
      <span class="value-current">{{ KPI.lastResult(kpi) }}</span>
      <span class="value-target" :style="{ color: targetColor }">/ {{ KPI.lastResultTarget(kpi) }} {{ $t('Webportal.Target') }}</span>
    </div>

    <!-- Footer -->
    <div class="card-footer">
      <span class="footer-text">
        {{ $t('Webportal.Prewave') }}: {{ KPI.secondLastResult(kpi) }}
      </span>
      <div :class="['status-indicator']" :style="[ 'background-color: ' + getStatusColor() ]" />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { MoreVertical, Trash2, Minus } from 'lucide-vue-next';
import { useKpiHelpers } from '@/composables/kpi-helpers';
import ThresholdColor from '@/mixins/ThresholdColor';

export default {
  name: 'KpiCard',

  mixins: [ThresholdColor],

  components: {
    MoreVertical,
    Trash2,
    Minus
  },

  props: {
    boardData:  { type: Object, required: true },
    control:    { type: Object, required: true }
  },

  watch: {
    boardData: {
      handler() { this.getKpi(this.boardData, this.control); },
      immediate: true
    }
  },

  data() {
    return {
      kpi: null,
      kpiGroup: null
    };
  },

  setup() {
    const KPI = useKpiHelpers();
    return { KPI };
  },

  computed: {
    ...mapGetters('clientData', [ 'client', 'indices' ]),

    targetColor() {
      const indexId = this.control?.indexId;
      if (indexId == null || indexId < 0) return null;
      const index = this.indices?.find(idx => idx.indexId === indexId);
      if (!index?.thresholds) return null;
      const lastVal = this.kpi?.SurveyWave?.at(-1)?.Result;
      if (lastVal == null) return null;
      const thre = index.thresholds.find(th => lastVal >= th.min && lastVal <= th.max);
      return thre?.color || null;
    },
  },

  emits: ['click-kpi', 'edit-kpi', 'delete-kpi'],

  methods: {
    getKpi(boardData, control) {
      if (!boardData || !control) return null;
      this.kpi = boardData.KPI ? boardData.KPI.find(k => k.KpiCode === control.kpiCode) : null;
      this.kpiGroup = boardData.KPIGroup ? boardData.KPIGroup.find(g => g.KpiCode === control.kpiGroupCode) : null;
    },

    handleClick() {
      this.$emit('click-kpi', this.kpi);
    },

    formatValue(value) {
      if (value == null) return '-';
      return `${value}%`;
    },
    getTrend() {
      const waves = this.kpi?.SurveyWave;
      if (!waves || waves.length < 2) return 0;

      const lastResult = waves[waves.length - 1]?.Result;
      const secondLastResult = waves[waves.length - 2]?.Result;

      if (lastResult == null || secondLastResult == null) return 0;

      const trend = lastResult - secondLastResult;
      if (trend === 0) return 0;

      const sign = trend > 0 ? '+' : '';
      return `${sign}${trend}`;
    },
    formatTrend() {
      return this.getTrend() + 'pp';
    },

    getStatusColor() {
      const waves = this.kpi?.SurveyWave;
      if (!waves || waves.length === 0) return 'gray';

      const lastResult = waves[waves.length - 1]?.Result;
      if (!lastResult) return 'gray';

      let col = this.getThresholdColor(this.client.Thresholds, lastResult);
      return col;
    }
  }
}
</script>

<style lang="scss" scoped>
.kpi-card {
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 15rem;
  gap: 12px;
  padding: 16px;
  background-color: var(--target-card-bg);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: rgba(2, 54, 112, 0.3);

    .card-actions {
      opacity: 1;
    }
  }
}

.card-actions {
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 10;
}

.card-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  background: white;
  border: 1px solid red;
  border-radius: 4px;
  color: red;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #f9fafb;
    color: #374151;
  }

  &-danger:hover {
    background: #fef2f2;
    border-color: #fecaca;
    color: #dc2626;
  }
}

.card-badges {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.card-badge {
  display: inline-flex;
  text-transform: uppercase;
  align-items: center;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 500;
  background: #f9fafb;
  color: var(--color-muted-foreground);
  border: 1px solid var(--color-border);
  border-radius: 4px;
}

.badge-separator {
  font-size: 12px;
  color: #d1d5db;
}

.card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  background-color: unset !important;
  padding: unset;
}

.card-header-left {
  flex: 1;
  min-width: 0;
}

.card-category {
  font-size: 12px;
  color: var(--color-muted-foreground);
  margin: 0 0 2px;
}

.card-title {
  font-size: 14px;
  font-weight: 500;
  color: var(--color-foreground);
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.no-break {
  white-space: nowrap;      /* kein Zeilenumbruch */
  overflow: hidden;         /* Überlauf abschneiden */
  text-overflow: ellipsis;  /* ... am Ende */
}
.card-trend {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--color-muted-foreground);
  white-space: nowrap;
}

.card-value {
  display: flex;
  align-items: baseline;
  gap: 8px;
  // margin: 4px 0;
}

.value-current {
  font-size: 24px;
  font-weight: 700;
  color: var(--target-primary);
}

.value-target {
  font-size: 12px;
  color: var(--color-muted-foreground);
}

.card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.footer-text {
  font-size: 12px;
  color: var(--color-muted-foreground);
}

.status-indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;

  &.status-green {
    background: var(--default-status-green);
  }

  &.status-yellow {
    background: var(--default-status-yellow);
  }

  &.status-red {
    background: var(--default-status-red);
  }

  &.status-gray {
    background: var(--default-status-indifferent);
  }
}

// Dark Mode
:root.dark,
[data-bs-theme="dark"] {
  .kpi-card {
    background: var(--color-card);
    border-color: var(--color-border);
  }

  .card-action-btn {
    background: var(--color-card);
    border-color: var(--color-border);
    color: var(--color-muted-foreground);

    &:hover {
      background: var(--color-muted);
      color: var(--color-foreground);
    }
  }

  .card-badge {
    background: var(--color-muted);
    color: var(--color-foreground);
    border-color: var(--color-border);
  }

  .card-category,
  .card-trend,
  .value-target,
  .footer-text {
    color: var(--color-muted-foreground);
  }

  .card-title {
    color: var(--color-foreground);
  }

  .value-current {
    color: var(--color-primary);
  }
}
</style>
